import type { AlgoliaHit, SearchProductPreview } from 'shared/src/types/search.type';
import type { LocaleDetails } from 'shared/src/types/languages.type';
import { normalizeProductName, productNamesFocus } from 'shared/src/lib/focus-products';
import { selectLocalizedUrl } from '@/lib/locale-url-selector';

/**
 * A product the conversation is about, as the catalogue has it: what the
 * comparison card shows above a column (photo, price) and where a click on it
 * goes (the product on its page, scrolled into view by `?buy=`).
 */
export interface FocusVisual {
  /** The shopper's name for it ("iPad Pro"), as the focus lookup matched it. */
  focusName: string;
  product: SearchProductPreview;
  url: string | null;
}

const withBuySlug = (pageUrl: string, product: SearchProductPreview): string =>
  product.buySlug ? `${pageUrl}${pageUrl.includes('?') ? '&' : '?'}buy=${product.buySlug}` : pageUrl;

/** Every named product the shelf's focus pages carry, in the order they were named. */
export function focusVisualsFromHits(hits: AlgoliaHit[], locale: LocaleDetails): FocusVisual[] {
  const out: FocusVisual[] = [];
  for (const hit of hits) {
    if (hit.pg18) continue;
    const pageUrl = selectLocalizedUrl(hit.href, locale) ?? null;
    for (const product of hit.products ?? []) {
      if (!product.focusName) continue;
      out.push({ focusName: product.focusName, product, url: pageUrl ? withBuySlug(pageUrl, product) : null });
    }
  }
  return out.sort((a, b) => (a.product.focusRank ?? 99) - (b.product.focusRank ?? 99));
}

/**
 * The catalogue product for one comparison column. The model usually writes
 * the column exactly as it wrote the focus name; when it is more specific
 * ("iPad Pro (M5)" for the focus "iPad Pro") the names still line up.
 */
export function visualForComparisonItem(item: string, visuals: FocusVisual[]): FocusVisual | undefined {
  const key = normalizeProductName(item);
  return (
    visuals.find((v) => normalizeProductName(v.focusName) === key) ??
    visuals.find(
      (v) =>
        productNamesFocus(v.focusName, item) ||
        productNamesFocus(item, v.product.title, v.product.brand),
    )
  );
}
